import { useState } from "react";

import { useMutation } from "@tanstack/react-query";
import { useReploAgent } from "@v2/contexts/ReploAgentContext";
import { useSearchParams } from "react-router-dom";
import { ReploValidationError } from "schemas/errors";

export function useGitPreview() {
  const {
    actions: { previewCommit, exitPreview },
  } = useReploAgent();

  const [searchParams] = useSearchParams();
  const sessionUuid = searchParams.get("sessionUuid");

  const [previewingSha, setPreviewingSha] = useState<string | null>(null);

  const previewMutation = useMutation({
    mutationFn: async (sha: string) => {
      if (!sessionUuid) {
        throw new ReploValidationError("No sessionUuid available for preview");
      }
      if (previewingSha === sha) {
        await exitPreview({ sessionUuid });
        return { action: "exited" as const };
      }
      await previewCommit({ sha, sessionUuid });
      return { action: "previewing" as const, sha };
    },
    onSuccess: (result) => {
      setPreviewingSha(result.action === "previewing" ? result.sha : null);
    },
  });

  const exitPreviewMutation = useMutation({
    mutationFn: async () => {
      if (!sessionUuid) {
        throw new ReploValidationError(
          "No sessionUuid available for exit preview",
        );
      }
      await exitPreview({ sessionUuid });
    },
    onSuccess: () => setPreviewingSha(null),
  });

  return {
    previewingSha,
    isLoadingPreview:
      previewMutation.isPending || exitPreviewMutation.isPending,
    isInPreviewMode: Boolean(previewingSha),
    handlePreview: previewMutation.mutate,
    handleExitPreview: exitPreviewMutation.mutateAsync,
  };
}
